//React
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
//Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
//Redux
import {
  useAddjobCardMutation,
  useGetJobCardByReferenceQuery,
  useEditJobCardMutation,
  useDeleteJobCardMutation,
  useAddChangeLogMutation,
} from "../api/apiSlice";
import { useDispatch } from "react-redux";
//Routing
import { useNavigate, useParams } from "react-router-dom";
//Components
import NavbarStepper from "./Stepper/NavBarStepper";
import { useConnectionMonitor } from "./connectionsTest";
import ConnectionAlert from "./ConnectionAlert";
import LoadingOverlay from "./LoadingOverlay";
import { useStepperStateRestoration } from "./useStepperStateRestoration";
import stepSaveMessage from "./stepSaveMessage";
import { showError } from "../../Global/notifications/notificationsSlice";
import config from "../../config";

const GOOGLE_MAPS_API_KEY = config.REACT_APP_GOOGLE_API_KEY;

const SECTION_SX = {
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.4)",
  mb: "14px",
};

const FIELD_LABEL_SX = {
  fontSize: "0.88rem",
  color: "rgba(255,255,255,0.7)",
  mb: "6px",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#1c2130",
    borderRadius: "8px",
    color: "#e0e6f0",
    fontSize: "0.95rem",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940", borderWidth: "1px" },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    "&::placeholder": { color: "rgba(255,255,255,0.25)", opacity: 1 },
  },
};

function ClientDetailsPage() {
  useStepperStateRestoration();
  const dispatch = useDispatch();

  const {
    isConnected,
    isFullyConnected,
    isWeakConnection,
    responseTime,
    avgResponseTime,
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    connectionDetails,
  } = useConnectionMonitor();

  const [address, setAddress] = React.useState("");
  const [property_type, setPropertyType] = React.useState("house");
  const [building_name, setBuildingName] = React.useState("");
  const [unit_number, setUnitNumber] = React.useState("");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");
  const [address_description, setAddressDescription] = React.useState("");
  // A resumed card's address that has no coordinates: shown as a hint, it must be picked again.
  const [previous_address, setPreviousAddress] = React.useState("");
  const [erf, setErf] = React.useState("");
  const [erfError, setErfError] = React.useState("");
  // Nothing picked until the installer chooses. It started on "Unwilling", so an abort from
  // here was recorded as a customer refusal whatever happened (stress test 26/10, P3).
  const [customer_consent, setCustomerConsent] = React.useState("");
  const [disabled_button, setDisabled] = React.useState(true);

  const [error_alert, setErrorAlert] = React.useState(false);
  const [error_message, setErrorMessage] = React.useState(false);
  const [abort_dialog_open, setAbortDialogOpen] = React.useState(false);
  const isAbortingRef = React.useRef(false);
  const [breaker_control, setbreaker_control] = React.useState(false);
  const [solar_geyser, setsolar_geyser] = React.useState(false);
  const [solar_pv, setsolar_pv] = React.useState(false);
  const [ripple_replacement, setripple_replacement] = React.useState(false);
  const [timer_installed, settimer_installed] = React.useState(false);
  const [heat_pump, setheatpump] = React.useState(false);
  const [ripple_bypass, setripplebypass] = React.useState(false);

  const { job_card_reference_id, serial_number, imsi_number } = useParams();
  const { data: job_card_state_data, isSuccess: isGetJobCardStateSuccess } = useGetJobCardByReferenceQuery(
    job_card_reference_id,
    { skip: !job_card_reference_id }
  );
  const [editJobCard, { data: job_card, isSuccess: isEditJobCardSuccess, isError: isEditJobCardError, error: editJobCardError, isLoading: isEditJobCardLoading }] = useEditJobCardMutation();
  // A replacement's details save could fail with nothing on screen (N24).
  useEffect(() => {
    if (isEditJobCardError) dispatch(showError(stepSaveMessage(editJobCardError)));
  }, [isEditJobCardError]);

  const setCoordinates = ({ latitude, longitude }) => {
    setLatitude(latitude);
    setLongitude(longitude);
  };

  useEffect(() => {
    if (isGetJobCardStateSuccess) {
      if (job_card_state_data.install_state === "RE" || job_card_state_data.install_state === "UN") {
        // The address must come with its location (N2). A card from before that rule may
        // have one without coordinates: it is picked again.
        const hasLocation = job_card_state_data.latitude != null && job_card_state_data.longitude != null;
        setAddress(hasLocation ? job_card_state_data.address : "");
        setPreviousAddress(hasLocation ? "" : job_card_state_data.address || "");
        setLatitude(hasLocation ? job_card_state_data.latitude : "");
        setLongitude(hasLocation ? job_card_state_data.longitude : "");
        setPropertyType(job_card_state_data.property_type || "house");
        setBuildingName(job_card_state_data.building_name || "");
        setUnitNumber(job_card_state_data.unit_number || "");
        setAddressDescription(job_card_state_data.address_description || "");
        setErf(job_card_state_data.erf_number || "");
        setCustomerConsent(job_card_state_data.customer_consent || "");
        setbreaker_control(job_card_state_data.breaker_control);
        setsolar_geyser(job_card_state_data.solar_geyser);
        setsolar_pv(job_card_state_data.solar_pv);
        setripple_replacement(job_card_state_data.ripple_replacement);
        settimer_installed(job_card_state_data.timer_installed);
        setheatpump(job_card_state_data.heat_pump);
        setripplebypass(job_card_state_data.ripple_bypass);
      }
    }
  }, [isGetJobCardStateSuccess, job_card_state_data]);

  const [addJobCard, { data: job_card_data, isSuccess: isAddJobCardSuccess, isError: isAddJobCardError, error: addJobCardError, isLoading: isAddJobCardLoading }] = useAddjobCardMutation();
  const [deleteJobCard, { isSuccess: isDeleteJobCardSuccess }] = useDeleteJobCardMutation();
  const [addChangeLog] = useAddChangeLogMutation();

  // Stable idempotency key for this install session. Persisted in sessionStorage so a
  // page refresh reuses the same key, letting the BE return the existing card.
  const clientRequestIdRef = useRef(null);
  useEffect(() => {
    let id = sessionStorage.getItem("installer_client_request_id");
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem("installer_client_request_id", id);
    }
    clientRequestIdRef.current = id;
  }, []);

  const isPageLoading = isEditJobCardLoading || isAddJobCardLoading;

  const handleChange = (event) => {
    const { name, checked } = event.target;
    switch (name) {
      case "breaker_control":   setbreaker_control(checked);   break;
      case "solar_geyser":      setsolar_geyser(checked);      break;
      case "solar_pv":          setsolar_pv(checked);          break;
      case "timer_installed":   settimer_installed(checked);   break;
      case "ripple_replacement": setripple_replacement(checked); break;
      case "heat_pump":         setheatpump(checked);          break;
      case "ripple_bypass":     setripplebypass(checked);      break;
      default: break;
    }
  };

  // The address counts only when it was picked from the suggestions, which is what gives it
  // its coordinates. Typed text, or a pick edited afterwards, does not (N2).
  const hasPickedAddress = address !== "" && latitude !== "" && latitude != null && longitude !== "" && longitude != null;
  const PICK_ADDRESS = "Pick the address from the suggestions list so the job has its location.";

  const handleOpenAbortDialog = () => {
    if (!hasPickedAddress) { setErrorMessage(PICK_ADDRESS); setErrorAlert(true); }
    else setAbortDialogOpen(true);
  };

  const handleCloseAbortDialog = () => setAbortDialogOpen(false);

  const handleChangeCustomerConsent = (event) => setCustomerConsent(event.target.value);

  let navigate = useNavigate();

  const ERF_RE = /^[A-Za-z0-9/\- ]*$/;

  const handleAddJobCard = () => {
    if (!hasPickedAddress) {
      setErrorMessage(PICK_ADDRESS); setErrorAlert(true);
    } else if (property_type === "complex" && unit_number === "") {
      setErrorMessage("Unit Number is required for Complex / Flat installs."); setErrorAlert(true);
    } else if (erf && (erf.length > 32 || !ERF_RE.test(erf))) {
      setErfError("Only letters, numbers, /, - and spaces are allowed (max 32 characters).");
    } else {
      setErfError("");
      const complexFields = property_type === "complex" ? { building_name, unit_number } : {};
      if (job_card_state_data?.install_state === "RE") {
        editJobCard({ body: { address, property_type, ...complexFields, latitude, longitude, address_description: address_description, erf_number: erf, customer_consent, breaker_control, solar_geyser, solar_pv, ripple_replacement, timer_installed, aborted: false, heat_pump, ripple_bypass, state: "AMP", reference_id: job_card_state_data.reference_id } });
      } else {
        addJobCard({ body: { address, property_type, ...complexFields, latitude, longitude, address_description: address_description, erf_number: erf, customer_consent, breaker_control, solar_geyser, solar_pv, ripple_replacement, timer_installed, aborted: false, heat_pump, ripple_bypass, client_request_id: clientRequestIdRef.current } });
      }
    }
  };

  const handleAbortJob = () => {
    isAbortingRef.current = true;
    localStorage.setItem("clientDetails", false);
    if (job_card_state_data?.install_state === "RE") {
      // For replacement cards the job card already exists — abort it directly
      // instead of creating a new ABO card. The consent goes with it so the tracker
      // shows a customer refusal, not an installer abort with no reason (N30).
      deleteJobCard({ body: { reference_id: job_card_state_data.reference_id, customer_consent } });
    } else {
      addJobCard({ body: { address, customer_consent, aborted: true } });
    }
  };

  useEffect(() => {
    if (isAddJobCardSuccess) {
      localStorage.setItem("clientDetails", false);
      sessionStorage.removeItem("installer_client_request_id");
      if (isAbortingRef.current) {
        isAbortingRef.current = false;
        navigate("/installer/");
      } else {
        navigate(`/installer/${job_card_data.job_card_reference_id}/qrcode/`);
      }
    }
  }, [isAddJobCardSuccess]);

  useEffect(() => {
    if (isDeleteJobCardSuccess) {
      // A replacement refused here started on the old device (QRScanUninstall), so its end
      // goes there too, as useAbortJob does for the other steps (P4).
      if (job_card_state_data?.archived_serial_number) {
        addChangeLog({ body: { serial_number: job_card_state_data.archived_serial_number,
                               change: `Replacement aborted: customer ${customer_consent.toLowerCase()}` } });
      }
      sessionStorage.removeItem("installer_client_request_id");
      isAbortingRef.current = false;
      navigate("/installer/");
    }
  }, [isDeleteJobCardSuccess]);

  useEffect(() => {
    if (isEditJobCardSuccess) {
      localStorage.setItem("clientDetails", false);
      navigate(`/installer/${job_card_reference_id}/${serial_number}/${imsi_number}/connection_test_interlude/`);
    }
  }, [isEditJobCardSuccess]);

  useEffect(() => {
    if (isAddJobCardError) { setErrorAlert(true); setErrorMessage(addJobCardError.data.error_message); }
  }, [isAddJobCardError]);

  useEffect(() => {
    setDisabled(customer_consent !== "Willing");
  }, [customer_consent]);
  // Abort here records the customer's refusal, so it needs one picked.
  const isRefusal = customer_consent === "Unwilling" || customer_consent === "Absent";

  return (
    <Box sx={{ bgcolor: "#13161f", height: "100vh", display: "flex", flexDirection: "column" }}>
      {showAlert && (
        <ConnectionAlert onRetry={handleRetry} onClose={handleDismiss} connectionDetails={connectionDetails} position="top" alertType="error" />
      )}

      {/* Weak Connection Warning - only shows when connection is slow */}
      {showWeakWarning && (
        <ConnectionAlert onRetry={handleRetry} onClose={handleWeakWarningDismiss} connectionDetails={connectionDetails} position="top" alertType="warning" responseTime={responseTime || avgResponseTime} />
      )}

      {/* Scrollable content — minHeight:0 lets flex shrink below content height */}
      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, position: "relative" }}>
        <LoadingOverlay isLoading={isPageLoading} />
        <NavbarStepper />
        <DialogAbort open={abort_dialog_open} handleCloseDialog={handleCloseAbortDialog} handleAbortJob={handleAbortJob} />

        <Box sx={{ px: "20px", maxWidth: 480, margin: "0 auto", mt: 0, pb: "16px" }}>

          {/* Section: Installation Details */}
          <Typography sx={SECTION_SX}>Installation Details</Typography>

          {/* Property Type */}
          <Typography sx={FIELD_LABEL_SX}>Property Type <span style={{ color: "#f87171" }}>*</span></Typography>
          <PropertyTypeToggle
            value={property_type}
            onChange={(val) => {
              setPropertyType(val);
              if (val === "house") { setBuildingName(""); setUnitNumber(""); }
            }}
          />

          {/* Address */}
          <Box sx={{ mt: "16px" }}>
            <Typography sx={FIELD_LABEL_SX}>Address <span style={{ color: "#f87171" }}>*</span></Typography>
            <AddressTextField setterVariable={address} setterFunction={setAddress} setCoordinates={setCoordinates} previousAddress={previous_address} />
          </Box>

          {/* Complex / Flat fields */}
          {property_type === "complex" && (
            <>
              <Box sx={{ mt: "16px" }}>
                <Typography sx={FIELD_LABEL_SX}>Building Name <span style={{ color: "#f87171" }}>*</span></Typography>
                <GenericTextField setterVariable={building_name} setterFunction={setBuildingName} placeholder="e.g. Sunset Apartments" />
              </Box>
              <Box sx={{ mt: "16px" }}>
                <Typography sx={FIELD_LABEL_SX}>Unit Number <span style={{ color: "#f87171" }}>*</span></Typography>
                <GenericTextField setterVariable={unit_number} setterFunction={setUnitNumber} placeholder="e.g. Unit 3B" />
              </Box>
            </>
          )}

          {/* Address description */}
          <Box sx={{ mt: "16px" }}>
            <Typography sx={FIELD_LABEL_SX}>Description (optional)</Typography>
            <GenericTextField setterVariable={address_description} setterFunction={setAddressDescription} />
          </Box>

          {/* Erf / Stand No */}
          <Box sx={{ mt: "16px" }}>
            <Typography sx={FIELD_LABEL_SX}>Erf / Stand No (optional)</Typography>
            <GenericTextField
              setterVariable={erf}
              setterFunction={setErf}
              placeholder="e.g. 1789"
              helperText={erfError || "Optional — if known, helps match this install to the municipal billing record."}
              error={erfError}
              onChange={() => erfError && setErfError("")}
            />
          </Box>

          {/* Customer Detail */}
          <Box sx={{ mt: "20px", mb: "8px" }}>
            <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.6)", mb: "10px" }}>
              Customer Detail — select one
            </Typography>
            <CustomerConsentButtons value={customer_consent} onChange={handleChangeCustomerConsent} />
            {!customer_consent && (
              <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", mt: "8px" }}>
                Select one to continue: Willing goes on to the install; Unwilling or Absent lets you abort and record it.
              </Typography>
            )}
          </Box>

          {/* Installation Detail Toggles */}
          <Typography sx={{ ...SECTION_SX, mt: "28px", mb: "0" }}>Installation Details</Typography>
          <Box sx={{ bgcolor: "#1c2130", borderRadius: "10px", overflow: "hidden" }}>
            <SwitchRow label="User switches breaker"   name="breaker_control"    checked={breaker_control}    onChange={handleChange} />
            <SwitchRow label="Solar Geyser"            name="solar_geyser"       checked={solar_geyser}       onChange={handleChange} />
            <SwitchRow label="Solar PV on roof"        name="solar_pv"           checked={solar_pv}           onChange={handleChange} />
            <SwitchRow label="Timer or device installed" name="timer_installed"  checked={timer_installed}    onChange={handleChange} />
            <SwitchRow label="Ripple Replacement"      name="ripple_replacement" checked={ripple_replacement} onChange={handleChange} />
            <SwitchRow label="Heat Pump"               name="heat_pump"          checked={heat_pump}          onChange={handleChange} />
            <SwitchRow label="Ripple Bypass"           name="ripple_bypass"      checked={ripple_bypass}      onChange={handleChange} last />
          </Box>

          <ErrorAlert error_alert={error_alert} setErrorAlert={setErrorAlert} error_message={error_message} />
        </Box>
      </Box>

      {/* Bottom buttons */}
      <Box sx={{
        flexShrink: 0,
        display: "flex",
        gap: "12px",
        px: "16px",
        py: "14px",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        bgcolor: "#13161f",
      }}>
        <Button
          variant="contained"
          onClick={handleOpenAbortDialog}
          disabled={!isRefusal || !isConnected}
          disableElevation
          sx={{
            flex: 1,
            py: "13px",
            borderRadius: "10px",
            backgroundColor: "#0d1117 !important",
            color: "#e0e6f0 !important",
            fontSize: "1rem",
            fontWeight: 600,
            textTransform: "none",
            border: "1px solid rgba(255,255,255,0.12)",
            "&:hover": { backgroundColor: "#161b22 !important" },
            "&.Mui-disabled": { opacity: 0.4 },
          }}
        >
          Abort
        </Button>
        <Button
          variant="contained"
          onClick={handleAddJobCard}
          disabled={disabled_button || !isConnected || isPageLoading}
          disableElevation
          sx={{
            flex: 1,
            py: "13px",
            borderRadius: "10px",
            backgroundColor: "#FDB940 !important",
            color: "#0d1117 !important",
            fontSize: "1rem",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "#e8a830 !important" },
            "&.Mui-disabled": { opacity: 0.55 },
          }}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
}



function AddressTextField({ setterVariable, setterFunction, setCoordinates, previousAddress }) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [notice, setNotice] = useState("");
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (setterVariable && !inputValue) setInputValue(setterVariable);
  }, [setterVariable]);

  useEffect(() => {
    const loader = new Loader({ apiKey: GOOGLE_MAPS_API_KEY, version: "beta", libraries: ["places"] });
    loader.load().then(() => {
      if (!window.google) return;
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    });
  }, []);

  const fetchSuggestions = (input) => {
    clearTimeout(debounceRef.current);
    if (!input || !window.google?.maps?.places?.AutocompleteSuggestion) { setOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: sessionTokenRef.current,
          locationRestriction: { north: -22.14, south: -34.83, west: 16.45, east: 32.89 },
        });
        setOptions(suggestions.map((s) => ({ label: s.placePrediction.text.toString(), prediction: s.placePrediction })));
      } catch { setOptions([]); }
    }, 300);
  };

  const clearPick = () => {
    setterFunction("");
    setCoordinates({ latitude: "", longitude: "" });
  };

  const handleSelect = async (_, option) => {
    if (!option || typeof option === "string") return;
    const place = option.prediction.toPlace();
    try {
      await place.fetchFields({ fields: ["location"] });
    } catch {
      place.location = null;
    }
    // Keep the suggestion text the user picked; an establishment's formattedAddress
    // is its postal address (e.g. "Shop B014 ...") and differs from what was shown.
    const addr = option.label;
    setInputValue(addr);
    if (place.location) {
      setterFunction(addr);
      setCoordinates({ latitude: place.location.lat(), longitude: place.location.lng() });
      setNotice("");
    } else {
      clearPick();
      setNotice("That suggestion has no location. Pick another.");
    }
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      inputValue={inputValue}
      filterOptions={(x) => x}
      onInputChange={(_, newValue, reason) => {
        setInputValue(newValue);
        if (reason !== "reset") {
          // Editing a picked address un-picks it, so the field never shows one address
          // while the job saves another (N2).
          if (setterVariable && newValue !== setterVariable) clearPick();
          fetchSuggestions(newValue);
        }
      }}
      onChange={handleSelect}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={previousAddress ? `Was: ${previousAddress} — pick it again` : "e.g. 14 Vergelegen Ave, Somerset West"}
          helperText={notice || (previousAddress ? "Pick the address from the suggestions so the job has its location." : "")}
          FormHelperTextProps={{ sx: { color: notice ? "#f87171" : "#FDB940", fontSize: "0.78rem", mx: 0, mt: "6px" } }}
          variant="outlined"
          fullWidth
          sx={{
            ...INPUT_SX,
            "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": { color: "rgba(255,255,255,0.4)" },
          }}
        />
      )}
    />
  );
}

function GenericTextField({ setterVariable, setterFunction, placeholder, helperText, error, onChange }) {
  return (
    <TextField
      onChange={(e) => { setterFunction(e.target.value); onChange?.(); }}
      value={setterVariable}
      placeholder={placeholder}
      variant="outlined"
      fullWidth
      sx={INPUT_SX}
      helperText={helperText}
      error={!!error}
      FormHelperTextProps={{
        sx: {
          color: error ? "#f87171" : "rgba(255,255,255,0.4)",
          fontSize: "0.78rem",
          mx: 0,
          mt: "6px",
        },
      }}
    />
  );
}

const TOGGLE_BTN_SX = {
  flex: 1,
  py: "10px",
  color: "rgba(255,255,255,0.65)",
  backgroundColor: "#1c2130",
  border: "1px solid rgba(255,255,255,0.14) !important",
  textTransform: "none",
  fontSize: "0.9rem",
  fontWeight: 500,
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: "#FDB940 !important",
    color: "#0d1117 !important",
    fontWeight: 700,
  },
  "&:hover:not(.Mui-selected)": { backgroundColor: "rgba(255,255,255,0.05)" },
};

function PropertyTypeToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, val) => val && onChange(val)}
      fullWidth
    >
      <ToggleButton value="house"   sx={TOGGLE_BTN_SX}>House</ToggleButton>
      <ToggleButton value="complex" sx={TOGGLE_BTN_SX}>Complex / Flat</ToggleButton>
    </ToggleButtonGroup>
  );
}

function CustomerConsentButtons({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      onChange={(_, val) => val && onChange({ target: { value: val } })}
      fullWidth
    >
      <ToggleButton value="Willing"   sx={TOGGLE_BTN_SX}>Willing</ToggleButton>
      <ToggleButton value="Unwilling" sx={TOGGLE_BTN_SX}>Unwilling</ToggleButton>
      <ToggleButton value="Absent"    sx={TOGGLE_BTN_SX}>Absent</ToggleButton>
    </ToggleButtonGroup>
  );
}

function SwitchRow({ label, name, checked, onChange, last }) {
  return (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: "16px",
      py: "4px",
      borderBottom: last ? "none" : "1px solid rgba(255,255,255,0.06)",
    }}>
      <Typography sx={{ fontSize: "0.95rem", color: "#e0e6f0" }}>{label}</Typography>
      <Switch
        checked={checked}
        onChange={onChange}
        name={name}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: "#fff",
            "& + .MuiSwitch-track": { bgcolor: "#FDB940", opacity: 0.85 },
          },
          "& .MuiSwitch-track": { bgcolor: "rgba(255,255,255,0.18)", borderRadius: "10px" },
        }}
      />
    </Box>
  );
}

function ErrorAlert({ error_alert, setErrorAlert, error_message }) {
  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setErrorAlert(false);
  };
  return (
    <Snackbar open={error_alert} autoHideDuration={6000} onClose={handleClose}>
      <Alert onClose={handleClose} severity="error" variant="filled" sx={{ width: "100%" }}>
        {error_message}
      </Alert>
    </Snackbar>
  );
}

function DialogAbort({ open, handleCloseDialog, handleAbortJob }) {
  return (
    <Dialog open={open}>
      <DialogTitle>Are you sure you want to abort the current installation?</DialogTitle>
      <DialogContent>
        <DialogContentText>You will need to start a new install.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} variant="text">Back</Button>
        <Button onClick={handleAbortJob} variant="contained">Confirm</Button>
      </DialogActions>
    </Dialog>
  );
}

export default React.memo(ClientDetailsPage);
